import { useEffect, useMemo, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { DashboardSection } from "@/components/dashboard-section";
import { FirmAvatar } from "@/components/firm-logo";
import { firmLogoUrl } from "@/lib/firm-logos";
import { listFirms, type FirmRow, type FirmPlatformRow } from "@/lib/api/firms";
import { Loader2, Check, X, Search } from "@/components/ui/icons";
import { PageHeader } from "@/components/page-header";
import { CoolIcon } from "@/components/ui/cool-icon";
import { StatRow } from "@/components/stat-card";
import { Input } from "@/components/ui/input";
import { PLATFORM_OPTIONS } from "@/components/add-account/types";
import { cn } from "@/lib/utils";

/**
 * Firms page — operator's fleet-health view.
 *
 * Different from the marketing firm grid on the landing: this is the
 * live operator dashboard. For each firm we surface the signals needed
 * to decide whether we can route accounts through it right now:
 *   · bot readiness (are terminal URLs configured?)
 *   · crawl freshness (when did firm_intel last see it?)
 *   · active account count (where the bot is actually working)
 *   · marketing metadata (rating, country, discount) so an operator
 *     can eyeball a firm without clicking through to the landing page.
 *
 * All data comes from /api/firms in one round-trip — the endpoint
 * LEFT JOINs firm_profiles + correlates firm_intel, accounts, and
 * firm_availability_history for us.
 */

const EASE = [0.16, 1, 0.3, 1] as const;

type PlatformFilter = "all" | string;
type MarketFilter = "all" | "forex_cfd" | "futures";

export function FirmsPage(): ReactNode {
  const [firms, setFirms] = useState<FirmRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [platform, setPlatform] = useState<PlatformFilter>("all");
  const [market, setMarket] = useState<MarketFilter>("all");

  useEffect(() => {
    listFirms()
      .then(setFirms)
      .catch((e) => {
        setFirms([]);
        setLoadError(e instanceof Error ? e.message : String(e));
      });
  }, []);

  const filtered = useMemo(() => {
    if (!firms) return [];
    const needle = query.trim().toLowerCase();
    const out = firms.filter((f) => {
      if (needle && !f.name.toLowerCase().includes(needle)) return false;
      if (market !== "all" && marketTypeForFirm(f) !== market) return false;
      if (platform !== "all") {
        const supported = f.supported_platforms ?? [];
        if (!supported.includes(platform)) return false;
      }
      return true;
    });
    // Sort: active accounts desc → fresh crawl desc → name asc.
    out.sort((a, b) => {
      const ac = a.active_account_count ?? 0;
      const bc = b.active_account_count ?? 0;
      if (ac !== bc) return bc - ac;
      const ax = a.last_crawled_at ?? 0;
      const bx = b.last_crawled_at ?? 0;
      if (ax !== bx) return bx - ax;
      return a.name.localeCompare(b.name);
    });
    return out;
  }, [firms, query, platform, market]);

  const stats = useMemo(() => {
    if (!firms) return null;
    const now = Date.now();
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
    const ready = firms.filter(firmHasAnyUrl).length;
    const stale = firms.filter((f) => {
      const t = f.last_crawled_at;
      return typeof t === "number" && now - t > SEVEN_DAYS;
    }).length;
    const accounts = firms.reduce((n, f) => n + (f.active_account_count ?? 0), 0);
    const futures = firms.filter((f) => marketTypeForFirm(f) === "futures").length;
    return {
      total: firms.length,
      ready,
      stale,
      accounts,
      futures,
    };
  }, [firms]);

  const platformFilters = useMemo(() => {
    const seen = new Set<string>();
    if (firms) {
      for (const f of firms) {
        for (const p of f.supported_platforms ?? []) seen.add(p);
      }
    }
    const arr = Array.from(seen);
    // Sort by PLATFORM_OPTIONS order so the pills read MT5 → TradeLocker → Match → …
    arr.sort((a, b) => platformRank(a) - platformRank(b));
    return arr;
  }, [firms]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Firms"
        subtitle="Prop Firm Fleet Status"
        icon={<CoolIcon name="Building_02" />}
        accentWords={["Firms"]}
      />

      {stats && (
        <StatRow
          items={[
            { label: "Total Firms", value: String(stats.total) },
            { label: "Bot-Ready", value: String(stats.ready) },
            {
              label: "Stale Crawls (>7d)",
              value: String(stats.stale),
              valueClassName: stats.stale > 0 ? "text-amber-400" : undefined,
            },
            { label: "Futures Firms", value: String(stats.futures) },
            { label: "Active Accounts", value: String(stats.accounts) },
          ]}
        />
      )}

      {firms !== null && firms.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search firms…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-9 pl-9"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <FilterPill
              label="All Markets"
              active={market === "all"}
              onClick={() => setMarket("all")}
            />
            <FilterPill
              label="Forex & CFD"
              active={market === "forex_cfd"}
              onClick={() => setMarket("forex_cfd")}
            />
            <FilterPill
              label="Futures"
              active={market === "futures"}
              onClick={() => setMarket("futures")}
            />
          </div>
          {platformFilters.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <FilterPill
                label="All"
                active={platform === "all"}
                onClick={() => setPlatform("all")}
              />
              {platformFilters.map((p) => (
                <FilterPill
                  key={p}
                  label={platformLabel(p)}
                  active={platform === p}
                  onClick={() => setPlatform(p)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <DashboardSection title="Firms">
        {firms === null ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Loader2 className="mb-3 h-5 w-5 animate-spin text-accent" />
            <p className="text-sm text-muted-foreground">Loading firms…</p>
          </div>
        ) : loadError ? (
          <p className="py-8 text-center text-sm text-destructive">
            Couldn't load firms: {loadError}
          </p>
        ) : firms.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No firms configured yet. Run the firm-seed script to populate.
          </p>
        ) : filtered.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No firms match your filters.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((firm, i) => (
              <FirmCard key={firm.id} firm={firm} index={i} />
            ))}
          </div>
        )}
      </DashboardSection>
    </div>
  );
}

function FirmCard({ firm, index }: { firm: FirmRow; index: number }): ReactNode {
  const hasUrl = firmHasAnyUrl(firm);
  const platforms = firm.platforms ?? [];
  const fresh = freshness(firm.last_crawled_at ?? null);
  const rating = typeof firm.rating === "number" ? firm.rating : null;
  const reviewCount = typeof firm.review_count === "number" ? firm.review_count : null;
  const discount =
    typeof firm.discount_pct === "number" && firm.discount_pct > 0
      ? Math.round(firm.discount_pct)
      : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.32, delay: Math.min(index, 12) * 0.03, ease: EASE }}
      className="flex flex-col gap-3 rounded-xl border border-border/40 bg-muted/20 p-4"
    >
      <div className="flex items-start gap-3">
        <FirmAvatar
          src={firmLogoUrl(firm.id) ?? ""}
          name={firm.name}
          size="default"
          className="shrink-0"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium text-foreground">{firm.name}</p>
            {firm.country && <CountryFlag code={firm.country} />}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
            {rating !== null ? (
              <span className="inline-flex items-center gap-1 font-mono tabular-nums">
                <span className="text-amber-400">★</span>
                {rating.toFixed(1)}
                {reviewCount !== null && reviewCount > 0 && (
                  <span className="text-muted-foreground/70">
                    ({formatCompact(reviewCount)})
                  </span>
                )}
              </span>
            ) : (
              <span className="text-muted-foreground/60">Unrated</span>
            )}
            {firm.max_allocation && (
              <>
                <span className="text-muted-foreground/40">·</span>
                <span className="font-mono tabular-nums">Max {firm.max_allocation}</span>
              </>
            )}
          </div>
        </div>
        {discount !== null && (
          <span
            className="shrink-0 rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-semibold text-accent"
            title={firm.offer_description ?? firm.discount_code ?? undefined}
          >
            {discount}% OFF
          </span>
        )}
      </div>

      <div>
        <span
          className={cn(
            "inline-flex rounded-md px-2 py-0.5 text-[10px] font-semibold",
            marketTypeForFirm(firm) === "futures"
              ? "bg-emerald-500/10 text-emerald-500"
              : "bg-accent/10 text-accent",
          )}
        >
          {marketTypeForFirm(firm) === "futures" ? "Futures Prop Firm" : "Forex & CFD Prop Firm"}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {platforms.length > 0 ? (
          platforms.map((p) => <PlatformChip key={p.platform_type} row={p} />)
        ) : (
          <span className="text-[11px] italic text-muted-foreground/60">
            No platforms mapped
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-border/40 pt-2 text-[11px]">
        <span
          className={cn(
            "inline-flex items-center gap-1.5",
            fresh.tone === "fresh" && "text-emerald-500",
            fresh.tone === "warn" && "text-amber-400",
            fresh.tone === "stale" && "text-destructive",
            fresh.tone === "never" && "text-muted-foreground/70",
          )}
          title={
            firm.last_crawled_at
              ? new Date(firm.last_crawled_at).toISOString()
              : "firm_intel has no rows for this firm"
          }
        >
          <span
            className={cn(
              "h-1.5 w-1.5 rounded-full",
              fresh.tone === "fresh" && "bg-emerald-500",
              fresh.tone === "warn" && "bg-amber-400",
              fresh.tone === "stale" && "bg-destructive",
              fresh.tone === "never" && "bg-muted-foreground/50",
            )}
          />
          {fresh.label}
        </span>
        <div className="flex items-center gap-3 text-muted-foreground">
          {hasUrl ? (
            <span
              className="inline-flex items-center gap-1 font-medium text-emerald-500"
              title="Terminal URL configured — bot can log in"
            >
              <Check className="h-3 w-3" /> Ready
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 font-medium"
              title="No terminal URL — operator must provide one per account"
            >
              <X className="h-3 w-3" /> No URL
            </span>
          )}
          {firm.active_account_count != null && firm.active_account_count > 0 && (
            <span className="font-mono tabular-nums text-foreground">
              {firm.active_account_count} acct
              {firm.active_account_count === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function PlatformChip({ row }: { row: FirmPlatformRow }): ReactNode {
  const meta = PLATFORM_OPTIONS.find((p) => p.type === row.platform_type);
  const hasUrl =
    !!row.login_url ||
    !!row.terminal_url ||
    !!row.terminal_url_demo ||
    !!row.terminal_url_funded;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[10px] font-medium",
        hasUrl
          ? "border-border/50 bg-card text-foreground"
          : "border-dashed border-muted-foreground/30 bg-transparent text-muted-foreground",
      )}
      title={hasUrl ? "URL seeded" : "No URL seeded for this platform"}
    >
      {meta?.logo && (
        <img
          src={meta.logo}
          alt=""
          aria-hidden
          className="h-3 w-3 object-contain"
        />
      )}
      {meta?.label ?? platformLabel(row.platform_type)}
    </span>
  );
}

function CountryFlag({ code }: { code: string }): ReactNode {
  const lower = code.trim().toLowerCase();
  if (!lower) return null;
  return (
    <span
      className={`fi fi-${lower} inline-block h-3 w-4 shrink-0 rounded-[2px]`}
      title={code.toUpperCase()}
      aria-label={`Country: ${code.toUpperCase()}`}
    />
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full px-3 py-1 text-[11px] font-medium transition-colors",
        active
          ? "bg-accent/15 text-accent ring-1 ring-inset ring-accent/30"
          : "bg-muted/40 text-muted-foreground hover:bg-muted/60 hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}

function firmHasAnyUrl(firm: FirmRow): boolean {
  if (firm.terminal_url || firm.terminal_url_demo || firm.terminal_url_funded) return true;
  for (const p of firm.platforms ?? []) {
    if (p.login_url || p.terminal_url || p.terminal_url_demo || p.terminal_url_funded) {
      return true;
    }
  }
  return false;
}

function marketTypeForFirm(firm: FirmRow): "forex_cfd" | "futures" {
  if (firm.market_type === "futures") return "futures";
  const platforms = firm.supported_platforms ?? firm.platforms?.map((p) => p.platform_type) ?? [];
  return platforms.some((p) => p === "tradovate" || p === "ninjatrader" || p === "blackarrow")
    ? "futures"
    : "forex_cfd";
}

type FreshnessTone = "fresh" | "warn" | "stale" | "never";

function freshness(ts: number | null): { label: string; tone: FreshnessTone } {
  if (ts == null || ts <= 0) return { label: "Never crawled", tone: "never" };
  const days = Math.max(0, Math.round((Date.now() - ts) / (24 * 60 * 60 * 1000)));
  if (days < 2) {
    return {
      label: days === 0 ? "Crawled today" : `Crawled ${days}d ago`,
      tone: "fresh",
    };
  }
  if (days <= 7) return { label: `Crawled ${days}d ago`, tone: "warn" };
  return { label: `Stale (${days}d)`, tone: "stale" };
}

function platformLabel(type: string): string {
  const meta = PLATFORM_OPTIONS.find((p) => p.type === type);
  return meta?.label ?? type;
}

function platformRank(type: string): number {
  const idx = PLATFORM_OPTIONS.findIndex((p) => p.type === type);
  return idx < 0 ? 99 : idx;
}

function formatCompact(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}
